import { useEffect, useState } from 'react'
import { continueWith, passkeysSupported, platformAuthenticatorAvailable } from '../lib/auth'
import { t } from '../lib/i18n'
import type { ApiError } from '../lib/api'
import Icon from './Icon'

/**
 * One field, one button.
 *
 * ── What this screen used to do, and why it changed ──────────────────────────
 *
 * It had a Sign in / Create account toggle, a name field, and a "Setup token"
 * box on the sign-up side. Three decisions to make before you could travel, two
 * of which the system should have made itself and one — the setup token — that
 * only ever applied to a single account on the whole deployment.
 *
 * Now: type a username, press the button. The server works out whether that is
 * a sign-in or a new account and hands back the right ceremony. Somebody who
 * cannot remember whether they made an account last summer does not have to.
 *
 * The setup code field does not exist until the server asks for it, which it
 * does only for the reserved owner handle. Nobody else is shown a box they have
 * no business filling in.
 */
export default function SignIn({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [handle, setHandle] = useState('')
  const [setupToken, setSetupToken] = useState('')
  const [needsSetupToken, setNeedsSetupToken] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // An error about the setup code renders against that field; everything else
  // renders above the button.
  const [errorField, setErrorField] = useState<'setup' | null>(null)
  const fieldError = errorField === 'setup' ? error : null

  // Whether WebAuthn exists at all decides what is shown; whether there is a
  // BUILT-IN authenticator only decides how the reassurance is worded.
  const supported = passkeysSupported()
  const [hasPlatform, setHasPlatform] = useState<boolean | null>(null)
  useEffect(() => { void platformAuthenticatorAvailable().then(setHasPlatform) }, [])

  async function go() {
    setBusy(true)
    setError(null)
    setErrorField(null)
    try {
      await continueWith({ handle: handle.trim(), setupToken: setupToken.trim() || undefined })
      await onSignedIn()
    } catch (err) {
      const code = (err as ApiError).code
      const name = (err as { name?: string }).name

      /*
       * There is no "you need a setup code" response to react to any more.
       *
       * The server used to answer 403 setup_token_required, so this form could
       * reveal the field only to somebody who needed it. That was the server
       * telling any stranger which username belongs to the administrator, for
       * free, to anyone who typed a name. Its answers are uniform now and the
       * field is reachable from the form itself — see the disclosure below.
       *
       * The cost is carried here: a wrong setup code is deliberately
       * indistinguishable from an unavailable username, so the owner sees this
       * message rather than "that code is wrong". `launch.mjs --verify` proves
       * the code against the live endpoint before anybody types it.
       */
      if (code === 'handle_taken') {
        setErrorField(null)
        setError(t('auth.handleTaken'))
      } else if (name === 'NotAllowedError' || name === 'AbortError') {
        setErrorField(null)
        // A dismissed system sheet is not a failure worth alarming anyone about.
        setError(t('auth.cancelled'))
      } else {
        setErrorField(null)
        setError((err as Error).message)
      }
    } finally {
      setBusy(false)
    }
  }

  if (!supported) {
    return <section className="card"><p>{t('auth.noPasskeySupport')}</p></section>
  }

  return (
    <section className="card signin">
      <h1>{t('auth.welcome')}</h1>
      <p className="muted">{t('auth.welcomeSub')}</p>

      <form onSubmit={(e) => { e.preventDefault(); if (handle.trim()) void go() }}>
        <label>
          <span>{t('auth.handle')}</span>
          <input
            value={handle}
            onChange={(e) => { setHandle(e.target.value.toLowerCase()); setError(null) }}
            autoComplete="username webauthn"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t('auth.handlePlaceholder')}
            autoFocus
          />
        </label>

        {/*
         * Always reachable, never revealed by the server.
         *
         * One line of muted text that almost nobody clicks is the price of not
         * having an endpoint that identifies the administrator's username. The
         * previous arrangement — server says "this one is reserved", client
         * shows the box — was a better form and a worse system.
         */}
        {!needsSetupToken && (
          <button type="button" className="link small" onClick={() => setNeedsSetupToken(true)}>
            {t('auth.haveSetupCode')}
          </button>
        )}

        {needsSetupToken && (
          <label className={`setup-token${fieldError ? ' has-error' : ''}`}>
            <span>{t('auth.setupToken')}</span>
            <input
              value={setupToken}
              onChange={(e) => { setSetupToken(e.target.value); setError(null) }}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={!!fieldError}
              aria-describedby={fieldError ? 'setup-error' : undefined}
              autoFocus
            />
            {/*
             * Against the field, and ABOVE the button.
             *
             * It used to sit below the submit, where the eye reaches the button
             * first and the message reads as a comment on the whole screen
             * rather than on the box that caused it — so somebody presses the
             * button again before noticing what is wrong with the input.
             */}
            {fieldError
              ? <span className="field-error" id="setup-error" role="alert">{fieldError}</span>
              : <span className="muted small">{t('auth.setupTokenWhy')}</span>}
          </label>
        )}

        {/* Errors that are not about one field still belong before the action,
            not after it. */}
        {error && !fieldError && <p className="error" role="alert">{error}</p>}

        <button className="primary with-icon" disabled={busy || !handle.trim()}>
          <Icon name="key" size={18} />
          {busy ? t('auth.working') : t('auth.continue')}
        </button>
      </form>

      {/*
        * Reassurance, not a warning.
        *
        * This used to read "…is the only way in", which is accurate and lands
        * like a locked door — the first thing a new person read was what they
        * could not do. The facts are unchanged; the framing is that a passkey is
        * a thing they already own and already use, which is the true and the
        * friendlier reading of the same sentence.
        */}
      <div className="reassure">
        <Icon name="shield" size={18} />
        <p>
          {hasPlatform === false ? t('auth.reassureKey') : t('auth.reassurePlatform')}
        </p>
      </div>
    </section>
  )
}
